import { AlertTriangle, Check, type LucideIcon, X } from 'lucide-react';

import type { OverviewBlockWidth } from './api/types';
import type { OverviewStatus } from './utils/resolveBlock';

// Volledige, letterlijke klassennamen: Tailwind genereert geen samengestelde namen.
// Raster: grid-cols-1 md:grid-cols-2 lg:grid-cols-4 (zie overviewGrid.tsx).
export const blockWidthClass: Record<OverviewBlockWidth, string> = {
  quarter: 'lg:col-span-1',
  half: 'md:col-span-2 lg:col-span-2',
  full: 'md:col-span-2 lg:col-span-4',
};

export const BLOCK_WIDTHS: readonly OverviewBlockWidth[] = ['quarter', 'half', 'full'];

export const blockWidthLabels: Record<OverviewBlockWidth, string> = {
  quarter: 'Kwart',
  half: 'Half',
  full: 'Volledig',
};

// Gevuld deel van het breedtebalkje in de breedtekeuze van de editor.
export const blockWidthBarClass: Record<OverviewBlockWidth, string> = {
  quarter: 'w-1/4',
  half: 'w-1/2',
  full: 'w-full',
};

// Meetraster voor rijen zonder kwartblok: rijen van GRID_ROW_PX zonder verticale gap; elk blok
// spant ceil((hoogte + GRID_GAP_PX) / GRID_ROW_PX) rijen. Moet kloppen met `auto-rows-[8px]`
// en `gap-x-4` in overviewGrid.tsx.
export const GRID_ROW_PX = 8;
export const GRID_GAP_PX = 16;

// Rijindeling (desktop): 4 kolommen; een blok neemt 1 (quarter), 2 (half) of 4 (full) kolommen.
export const GRID_COLUMNS = 4;

export const blockColumns: Record<OverviewBlockWidth, number> = {
  quarter: 1,
  half: 2,
  full: 4,
};

// Vaste 4 kolommen, voor de voorbeeldweergave in de editor (de breakpoints hierboven volgen de
// viewport, niet het smalle paneel in de modal).
export const blockWidthDesktopClass: Record<OverviewBlockWidth, string> = {
  quarter: 'col-span-1',
  half: 'col-span-2',
  full: 'col-span-4',
};

// Lege kolommen achter het laatste blok van een rij die niet vol is (hint in de editor).
export const emptyColumnsClass: Record<number, string> = {
  1: 'col-span-1',
  2: 'col-span-2',
  3: 'col-span-3',
};

// Zelfde maximum als Validatie (MAX_VISIBLE_ROWS in report/components/validationResult.tsx).
// Lokaal heeft ~95% van de criteria ≤ 3 waarden.
export const MAX_VISIBLE_VALUES = 3;

export const statusLabels: Record<OverviewStatus, string> = {
  present: 'Aanwezig',
  inconsistent: 'Inconsistent',
  missing: 'Niet aanwezig',
};

// Uitleg in de tooltip bij het statusicoon; volgt blockStatus() in utils/resolveBlock.ts.
export const statusDescriptions: Record<OverviewStatus, string> = {
  present: 'Er is een waarde gevonden in de documenten.',
  inconsistent: 'De documenten geven tegenstrijdige waarden. Controleer de bronnen.',
  missing: 'Er is geen waarde gevonden in de documenten.',
};

// Zelfde semantiek als Validatie en de groepskop op Informatie: gevonden = success + Check,
// inconsistent = warning + AlertTriangle, niet gevonden = error + X.
export const statusBadgeColor: Record<OverviewStatus, 'success' | 'warning' | 'error'> = {
  present: 'success',
  inconsistent: 'warning',
  missing: 'error',
};

export const statusIcon: Record<OverviewStatus, LucideIcon> = {
  present: Check,
  inconsistent: AlertTriangle,
  missing: X,
};

export const statusCardClass: Record<OverviewStatus, string> = {
  present: '',
  inconsistent: 'border-warning-300',
  missing: '',
};

// Uitklapknoppen ("Toon alle", "Meer lezen") in neutraal grijs, met dezelfde
// hover-achtergrond als de items in de sidebar.
export const toggleButtonClass =
  'h-7 px-1.5 text-neutral-700 hover:bg-neutral-200/50 hover:text-neutral-900 print:hidden';
